import { useState } from "react"
import { createPortal } from "react-dom"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { Comment, CommentReactionSummary } from "@nova/schemas"
import { useAddReaction, useRemoveReaction } from "features/comments/Comments"
import { useAppSelector } from "integrations/redux/hooks"
import { selectUserID } from "user/profile/selectors"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { EmojiPickerDropdown } from "plugins/Toolbox/MapComments/EmojiPickerDropdown"

type MapComment = Comment<"map-pin" | "map-comment">

export const useCommentReactions = (comment: MapComment) => {
    const [pickerOpen, setPickerOpen] = useState(false)
    const hasReactions = !!comment.reactions.length

    return {
        pickerOpen,
        setPickerOpen,
        // With no reactions yet the inline row is empty, so the add button
        // lives in the card header instead of under the text.
        addButtonInHeader: !hasReactions,
    }
}

const ReactionPill = ({
    reaction,
    didReact,
    onToggle,
}: {
    reaction: CommentReactionSummary
    didReact: boolean
    onToggle: () => void
}) => {
    const members = useAppSelector(selectWorkspaceMembers)
    const [anchor, setAnchor] = useState<{ top: number; left: number }>()
    const names = reaction.userIds.map(
        (userId) => members.find((member) => member.userId === userId)?.fullName ?? "Unknown user"
    )

    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation()
                    onToggle()
                }}
                onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    setAnchor({ top: rect.bottom + 5, left: rect.left + rect.width / 2 })
                }}
                onMouseLeave={() => setAnchor(undefined)}
                className={classNames(
                    "flex items-center gap-1 px-2 py-0.5 rounded-lg text-white text-xs border",
                    didReact ? "bg-white-300 border-blue-500" : "bg-white-200 border-white-200"
                )}
            >
                <span>{reaction.emoji}</span>
                <span>{reaction.userIds.length}</span>
            </button>
            {anchor &&
                createPortal(
                    <div
                        className="fixed -translate-x-1/2 pointer-events-none bg-gray-900 border border-gray-700 rounded-lg shadow-tooltip px-2 py-1 whitespace-nowrap"
                        style={{ top: anchor.top, left: anchor.left, zIndex: 9999 }}
                    >
                        {names.map((name, i) => (
                            <small key={i} className="block text-gray-50">
                                {name}
                            </small>
                        ))}
                    </div>,
                    document.getElementById("portal") ?? document.body
                )}
        </>
    )
}

export const AddReactionButton = ({
    comment,
    onOpenChange,
}: {
    comment: MapComment
    onOpenChange: (open: boolean) => void
}) => {
    const { addReaction } = useAddReaction()

    return (
        <EmojiPickerDropdown
            onSelect={(emoji) => addReaction({ commentId: comment.id, emoji })}
            onOpenChange={onOpenChange}
            className="size-6 rounded-lg text-gray-300 hover:text-white"
        >
            <Icons.SmallIcon Icon={Icons.AddReaction} className="size-[18px]" />
        </EmojiPickerDropdown>
    )
}

export const CommentReactions = ({
    comment,
    onPickerOpenChange,
}: {
    comment: MapComment
    onPickerOpenChange: (open: boolean) => void
}) => {
    const meUserId = useAppSelector(selectUserID) ?? ""
    const { addReaction } = useAddReaction()
    const { removeReaction } = useRemoveReaction()

    if (!comment.reactions.length) return null

    return (
        <div className="flex items-center flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
            {comment.reactions.map((reaction) => {
                const didReact = reaction.userIds.includes(meUserId)
                return (
                    <ReactionPill
                        key={reaction.emoji}
                        reaction={reaction}
                        didReact={didReact}
                        onToggle={() =>
                            didReact
                                ? removeReaction({ commentId: comment.id, emoji: reaction.emoji })
                                : addReaction({ commentId: comment.id, emoji: reaction.emoji })
                        }
                    />
                )
            })}
            <AddReactionButton comment={comment} onOpenChange={onPickerOpenChange} />
        </div>
    )
}
